import React, { useMemo } from 'react';
import { usePrintSettings } from '../../context/PrintSettingsContext';
import { challanPrintOverrideCss } from '../../utils/challanPrintLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { coilLineHtml } from '../../utils/printHelpers';
import { escapeHtml } from '../../utils/escapeHtml';
import { companyHeader } from '../../utils/company';

const formatCurrency = (v) => {
  if (v === null || typeof v === 'undefined' || v === '') return '₹ 0.00';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatNumber = (v) => {
  if (v === null || typeof v === 'undefined' || v === '') return '0';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-IN');
};

const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return d;
  }
};

const PurchaseChallanPrintModal = ({ isOpen, onClose, data, loading, onMarkChallanPrinted }) => {
    const { purchaseChallanPaperSize: challanPaperSize } = usePrintSettings();
  const printableHtml = useMemo(() => {
    if (!data) return '';
    const safe = (v) => (v === null || typeof v === 'undefined' || v === '' ? '-' : escapeHtml(v)); // print window (P1-2)
    const num = (v) => parseFloat(v) || 0;

    const dueDate = data.due_date ? formatDate(data.due_date) : null;

    // Financial mapping from JSON
    const itemTotal = num(data.total_value);
    const expenses = num(data.expenses);
    const discount = num(data.discount);
    const intermediateSubtotal = itemTotal + expenses - discount;

    const itemsGst = num(data.total_gst_amount);
    const expenseGst = num(data.expense_gst_amount);
    const discountGst = num(data.discount_gst_amount);
    const totalGst = itemsGst + expenseGst - discountGst;

    const roundingOff = num(data.rounding_off || 0);
    const grandTotal = intermediateSubtotal + totalGst + roundingOff;

    const items = Array.isArray(data.items) ? data.items.filter(it => it && Object.keys(it).length > 0) : [];
    const itemsRows = items.map((it, idx) => `
      <tr>
        <td style="text-align: center;">${idx + 1}</td>
        <td>
          <div style="font-weight: 700; font-size: 9px;">${safe(it.product_name)}</div>
          <div style="font-weight: 700; font-size: 9px;">${safe(it.size_name)}</div>
          ${coilLineHtml(it)}
        </td>
        <td style="text-align: center;">${it.actual_pieces || '-'}</td>
        <td style="text-align: right;">${formatNumber(it.actual_quantity)} ${it.unit}</td>
        <td style="text-align: center;">${it.gst_percentage}%</td>
        <td style="text-align: right;">${formatCurrency(it.price).replace('₹', '')}</td>
        <td style="text-align: right;">${formatCurrency(it.amount).replace('₹', '')}</td>
      </tr>`).join('');

    const companyDetailsHtml = [
      process.env.REACT_APP_COMPANY_ADDRESS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_ADDRESS}</div>` : '',
      (process.env.REACT_APP_COMPANY_GST || process.env.REACT_APP_COMPANY_PAN) ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${[process.env.REACT_APP_COMPANY_GST ? 'GST: ' + process.env.REACT_APP_COMPANY_GST : '', process.env.REACT_APP_COMPANY_PAN ? 'PAN: ' + process.env.REACT_APP_COMPANY_PAN : ''].filter(Boolean).join(' | ')}</div>` : '',
      process.env.REACT_APP_COMPANY_BANK_DETAILS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_BANK_DETAILS}</div>` : '',
    ].filter(Boolean).join('');

    return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Purchase Challan - ${safe(data.challan_number)}</title>
  <style>
    @page { size: A5 landscape; margin: 3mm; }
    * { box-sizing: border-box; font-family: sans-serif; color: #000; }
    body { margin: 0; padding: 0; font-size: 8.5px; line-height: 1.2; }
    .no-print { text-align: center; padding: 10px; background: #f5f5f5; border-bottom: 1px solid #ddd; }
    .print-container { width: 100%; max-width: 148mm; margin: 0 auto; color: #000; }
    .header-table { width: 100%; border: 1px solid #000; margin-bottom: 4px; }
    .header-table td { padding: 3px; vertical-align: top; border: 0.5px solid #ccc; }
    .title { text-align: center; font-size: 11px; font-weight: bold; border-bottom: 1px solid #000; padding: 2px 0; background: #eee; }
    
    table.data-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
    table.data-table th, table.data-table td { border: 1px solid #000; padding: 2px 4px; }
    table.data-table th { background: #eee; font-weight: bold; text-transform: uppercase; font-size: 7px; }
    
    .footer-section { display: flex; justify-content: space-between; gap: 20px; }
    .summary-table { width: 220px; border-collapse: collapse; }
    .summary-table td { padding: 2px 4px; border: 0.5px solid #eee; }
    .grand-total { border-top: 2.5px double #000 !important; font-weight: 900; font-size: 10.5px; background: #eee; }
    .subtotal-row { border-top: 1px solid #000; border-bottom: 1px solid #000; font-weight: 700; background: #fafafa; }
    
    @media print { .no-print { display: none !important; } }
  ${challanPrintOverrideCss(challanPaperSize, 'landscape')}
  </style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()" style="padding: 6px 16px; cursor: pointer;">PRINT CHALLAN</button></div>
  <div class="print-container">
    <div style="text-align: center; font-weight: bold; font-size: 14px; margin-bottom: 2px; text-transform: uppercase;">${safe(companyHeader(data.warehouse_name ? data.warehouse_name.toUpperCase() : ''))}</div>
    ${companyDetailsHtml}
    <div class="title" style="margin-bottom: 8px;">PURCHASE CHALLAN</div>
    
    <table class="header-table">
      <tr>
        <td style="width: 63%;">
          <div style="font-size: 7px; color: #666; font-weight: bold;">SUPPLIER:</div>
          <div style="font-weight: bold; font-size: 9px;">${safe(data.supplier_name)}</div>
          <div style="margin-top: 4px;">PO NUMBER: ${safe(data.po_number)}</div>
          <div style="margin-top: 2px;">VEHICLE: ${safe(data.vehicle_number)} | ${safe(data.driver_contact)}</div>
        </td>
        <td>
          <div style="font-size: 7px; color: #666; font-weight: bold;">CHALLAN NO:</div>
          <div style="font-weight: bold;">${safe(data.challan_number)}</div>
          <div style="margin-top: 4px;">DATE: ${formatDate(data.challan_date)}</div>
          <div style="margin-top: 2px; font-size: 7px;">WT: ${formatNumber(data.empty_truck_weight)} / ${formatNumber(data.loaded_truck_weight)} (Net: ${formatNumber(data.truck_weight)} KG)</div>
          ${data.payment_terms ? `<div style="margin-top: 2px; font-size: 7px;">PAYMENT TERMS: ${safe(data.payment_terms)}</div>` : ''}
          ${dueDate ? `<div style="margin-top: 2px; font-size: 7px;">DUE DATE: ${dueDate}</div>` : ''}
          ${data.challan_generated_by_name ? `<div style="margin-top: 2px; font-size: 7px;">GENERATED BY: ${safe(data.challan_generated_by_name)}</div>` : ''}
        </td>
      </tr>
    </table>

    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 20px;">Sr.</th>
          <th>Item Description</th>
          <th style="width: 30px;">Pcs</th>
          <th style="width: 60px;">Received Qty</th>
          <th style="width: 30px;">GST</th>
          <th style="width: 60px;">Rate (₹)</th>
          <th style="width: 70px;">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <div class="footer-section">
      <div style="flex-grow: 1;">
        <div style="margin-top: 6px; font-size: 7px; color: #666;">Invoice: ${safe(data.supplier_invoice_number)} | Dated: ${formatDate(data.supplier_invoice_date)}</div>
        <div style="margin-top: 2px; font-size: 7px; color: #666;">Total Received Weight: ${formatNumber(data.total_received_quantity)} KG</div>

        ${data.remark ? `<div style="margin-top: 4px; font-size: 8px; font-weight: bold; border: 1px dashed #ccc; padding: 3px; white-space: pre-wrap; background-color: #fef9c3;">REMARK: ${safe(data.remark)}</div>` : ''}
      </div>
      <div>
        <table class="summary-table">
          <tr><td>Item Total</td><td style="text-align: right;">${formatCurrency(itemTotal)}</td></tr>
          ${expenses > 0 ? `<tr><td>Expense</td><td style="text-align: right;">${formatCurrency(expenses)}</td></tr>` : ''}
          ${discount > 0 ? `<tr><td>Discount</td><td style="text-align: right; color: #d00;">- ${formatCurrency(discount)}</td></tr>` : ''}
          <tr class="subtotal-row"><td>Subtotal</td><td style="text-align: right;">${formatCurrency(intermediateSubtotal)}</td></tr>
          <tr><td>+GST</td><td style="text-align: right;">${formatCurrency(totalGst)}</td></tr>
          ${roundingOff !== 0 ? `<tr><td style="color:#6b7280;">Rounding Off</td><td style="text-align: right; color:#6b7280;">${formatCurrency(roundingOff)}</td></tr>` : ''}
          <tr class="grand-total"><td>Grand Total</td><td style="text-align: right;">${formatCurrency(grandTotal)}</td></tr>
        </table>
      </div>
    </div>
  </div>
</body>
</html>`;
  }, [data, challanPaperSize]);

  if (!isOpen) return null;

  // UI Totals Mapping
  const itemTotal = Number(data?.total_value || 0);
  const expenses = Number(data?.expenses || 0);
  const discount = Number(data?.discount || 0);
  const intermediateSubtotal = itemTotal + expenses - discount;

  const totalGst = Number(data?.total_gst_amount || 0) + Number(data?.expense_gst_amount || 0) - Number(data?.discount_gst_amount || 0);
  const roundingOff = Number(data?.rounding_off || 0);
  const grandTotal = intermediateSubtotal + totalGst + roundingOff;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl flex flex-col overflow-hidden max-h-[90vh]">
        {/* Simple Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gray-50">
          <h2 className="text-lg font-bold">Purchase Challan - {data?.challan_number || 'PC-XXXX'}</h2>
          <div className="flex gap-2">
            <button onClick={() => {
              const w = window.open('', '_blank');
              if (!w) {
                // Pop-up blocked — w is null; w.document.* would throw an uncaught
                // TypeError and the print would die silently. Tell the user instead.
                alert('Please allow pop-ups for this site to print.');
                return;
              }
              w.document.open();
              w.document.write(printableHtml);
              w.document.close();
              setTimeout(() => { w.focus(); w.print(); }, 300);
              if (onMarkChallanPrinted) onMarkChallanPrinted();
            }} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700">
              <FontAwesomeIcon icon={faPrint} /> Print
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 rounded">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Plain Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {loading ? (
            <div className="py-12 text-center text-gray-500"><FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Loading...</div>
          ) : data ? (
            <>
              {/* Basic Details Table */}
              <table className="w-full text-sm border-collapse">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500 w-1/4">Supplier</td>
                    <td className="py-2">{data.supplier_name}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500">PO Details</td>
                    <td className="py-2">PO: {data.po_number} | Invoice: {data.supplier_invoice_number}</td>
                  </tr>

                  {data.remark && (
                    <tr className="border-b">
                      <td className="py-2 font-bold text-gray-500">Purchase Challan Remark</td>
                      <td className="py-2">
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                          {data.remark}
                        </span>
                      </td>
                    </tr>
                  )}
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500">Vehicle</td>
                    <td className="py-2">{data.vehicle_number} ({data.driver_contact})</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-500">Weights (KG)</td>
                    <td className="py-2">Net: {formatNumber(data.truck_weight)} (Empty: {formatNumber(data.empty_truck_weight)} / Loaded: {formatNumber(data.loaded_truck_weight)})</td>
                  </tr>
                </tbody>
              </table>

              {/* Items Table */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left border-b border-t">
                    <th className="py-2 px-1">Item</th>
                    <th className="py-2 px-1 text-center">Pcs</th>
                    <th className="py-2 px-1 text-right">Received</th>
                    <th className="py-2 px-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items?.map((it, idx) => (
                    <tr key={idx}>
                      <td className="py-2 px-1">
                        <div className="font-medium text-gray-900">{it.product_name}</div>
                        <div className="text-xs text-gray-400">{it.size_name}</div>
                        {it.remark && it.remark !== 'none' && (
                          <div className="mt-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-800 border border-yellow-200 uppercase tracking-tight">
                            {it.remark}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-1 text-center">{it.actual_pieces}</td>
                      <td className="py-2 px-1 text-right">{formatNumber(it.actual_quantity)} {it.unit}</td>
                      <td className="py-2 px-1 text-right font-medium">{formatCurrency(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals Section */}
              <div className="flex justify-end">
                <div className="w-72 space-y-2 text-sm border-t pt-4">
                  <div className="flex justify-between"><span>Item Total</span><span>{formatCurrency(itemTotal)}</span></div>
                  {expenses > 0 && <div className="flex justify-between"><span>Expense</span><span>{formatCurrency(expenses)}</span></div>}
                  {discount > 0 && <div className="flex justify-between text-red-600"><span>Discount</span><span>- {formatCurrency(discount)}</span></div>}
                  <div className="flex justify-between font-bold bg-gray-50 px-2 py-1"><span>Subtotal</span><span>{formatCurrency(intermediateSubtotal)}</span></div>
                  <div className="flex justify-between"><span>+GST</span><span>{formatCurrency(totalGst)}</span></div>
                  {roundingOff !== 0 && <div className="flex justify-between text-gray-500"><span>Rounding Off</span><span>{formatCurrency(roundingOff)}</span></div>}
                  <div className="flex justify-between font-black text-xl border-t-2 border-gray-900 pt-2 mt-2">
                    <span>Grand Total</span>
                    <span>{formatCurrency(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-gray-500">No data found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseChallanPrintModal;
